class InfinitePagination extends HTMLElement {
  constructor() {
    super();

    this.paginationEvent = this.dataset.paginationEvent;
    this.url             = this.dataset.nextPage;
    this.button          = document.getElementById('loadMore');
    this.productLoaded   = document.getElementById('LoadMoreCurrent');
    this.loader          = document.querySelector('.pagination-loader');

    // guard against double‐fetch
    this._isFetching = false;

    if (this.paginationEvent === 'load_more') {
      this.button.addEventListener('click', () => this.fetchProducts());
    } else if (this.paginationEvent === 'infinite') {
      const handleIntersection = (entries) => {
        if (!entries[0].isIntersecting) return;
        if (!this.url) {
          // no more pages → stop observing
          if (this._infiniteObserver) this._infiniteObserver.unobserve(this.button);
          return;
        }
        this.fetchProducts();
      };
      this._infiniteObserver = new IntersectionObserver(
        handleIntersection.bind(this),
        { rootMargin: '0px 0px 400px 0px' }
      );
      this._infiniteObserver.observe(this.button);
    }
  }

  updateLoadedProductNumber() {
    const current = Number(this.productLoaded.textContent);
    const size    = Number(this.productLoaded.dataset.loadMoreSize);
    const total   = Number(this.productLoaded.dataset.loadMoreTotalSize);
    this.productLoaded.textContent =
      current + size >= total ? total : current + size;
  }

  lazyload(pictures) {
    if (!('IntersectionObserver' in window)) return;
    const animObserver = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('animate-fade-in');
        obs.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px 100px 0px'
    });
    pictures.forEach(pic => animObserver.observe(pic));
  }

  fetchProducts() {
    // skip if no more pages or a fetch is already in flight
    if (!this.url || this._isFetching) return;
    this._isFetching = true;
    this.loader.removeAttribute('hidden');

    fetch(this.url)
      .then(r => r.text())
      .then(htmlString => {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(htmlString, 'text/html');
        const temp   = doc.querySelector('#product-grid');
        if (!temp) return;

        const cleanedHTML = temp.innerHTML.trim();
        if (!cleanedHTML) return;

        // 1) inject new products
        const realGrid = this.querySelector('#product-grid');
        realGrid.insertAdjacentHTML('beforeend', cleanedHTML);

        // 2) eager-load images
        const newPics = Array.from(
          realGrid.querySelectorAll('picture.lazy-load:not(.loaded)')
        );
        newPics.forEach(pic => {
          pic.querySelectorAll('source').forEach(src => {
            if (src.dataset.srcset) {
              src.srcset = src.dataset.srcset;
              src.removeAttribute('data-srcset');
            }
          });
          const img = pic.querySelector('img');
          if (img.hasAttribute('loading')) img.removeAttribute('loading');
          const url = img.dataset.src || img.src;
          img.src = url;
          img.removeAttribute('data-src');
        });

        // 3) wait for all to finish loading
        const loadPromises = newPics.map(pic => {
          return new Promise(resolve => {
            const img = pic.querySelector('img');
            if (img.complete && img.naturalWidth !== 0) {
              return resolve();
            }
            // Resolve on load **or** error
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', () => {
              img.classList.add('img-404');
              resolve();
            }, { once: true });
          });
        });


        return Promise.all(loadPromises).then(() => ({ doc, newPics }));
      })
      .then(result => {
        if (!result) return;
        const { doc, newPics } = result;

        // 4) animate in the new pics
        this.lazyload(newPics);

        // 5) update UI & history
        this.loader.setAttribute('hidden', '');
        this.updateLoadedProductNumber();
        history.pushState(
          { scrollTop: document.body.scrollTop },
          '',
          this.url
        );

        // 6) grab the next-page URL out of the fetched HTML
        const nextPaginate = doc.querySelector('infinite-pagination');
        this.url = nextPaginate?.dataset.nextPage || '';
        this.dataset.nextPage = this.url;

        // 7) if no more pages, hide & unobserve
        if (!this.url) {
          this.button.setAttribute('hidden', '');
          if (this._infiniteObserver) {
            this._infiniteObserver.unobserve(this.button);
          }
        }

        // Call mobile slider
        mobileSlider();
      })
      .catch(console.error)
      .finally(() => {
        // allow future fetches
        this._isFetching = false;
      });
  }
}

customElements.define('infinite-pagination', InfinitePagination);
